<script>
import { validate } from '../../../../helpers/forms';
import { isPlainObject, cloneObject } from '../../../../helpers/common';
import { roundToNearestMinutesWithOptions } from 'date-fns/fp';

export default {
    props: {
        childrenAlwaysMultiple: {
            type: Boolean,
            default: false
        },
        hasEditableChildren: {
            type: Boolean,
            default: true
        },
        initialData: {
            type: Object,
            default() {
                return {};
            }
        },
        initialErrors: {
            type: Object | Array,
            default() {
                return null;
            }
        },
        childrenValidations: {
            type: Object,
            default() {
                return {};
            }
        },
        childrenValidationMessages: {
            type: Object | Function,
            default() {
                return {};
            }
        },
        ruleValidationMessages:  {
            type: Object | Function,
            default() {
                return {};
            }
        },
        // If the children data should get updated after a child got removed (=remove the child's data from the childrenData object)
        updateDataOnChildRemove: {
            type: Boolean,
            default: false
        },
        defaultChildValue: undefined
    },
    data() {
        return {
            childrenData: cloneObject(this.initialData),
            childrenInstances: {},
            alerts: []
        }
    },
    watch: {
        initialData: function(initialData) {
            if(initialData !== undefined) {
                this.setChildrenData(cloneObject(initialData));
            }
        },
        initialErrors: function(errors) {
            this.setErrors(errors);
        },
        state: function(newState) {
            this.allChildrenInstances.forEach(child => {
                if(child.inheritState) {
                    child.state = newState;
                }
            });
        }
    },
    created() {
        this.$on('childCreated', this.onChildCreated);
    },
    mounted() {
        if(this.initialErrors) {
            this.setErrors(this.initialErrors);
        }
    },
    beforeDestroy() {
        this.ignoreDestroyedChildren = true;
    },
    computed: {
        allChildrenInstances() {
            return Object.values(this.childrenInstances)
                .reduce((all, curr) => all.concat(curr), [])
                .sort((a, b) => a.index < b.index ? -1 : 1);
        },
        formData() {
            return Object.keys(this.childrenData).length == 1 && (undefined in this.childrenData) ? this.childrenData[undefined] : this.childrenData;
        }
    },
    methods: {
        onChildCreated(child) {
            this.childCreated(child);
        },
        childCreated(child) {
            this.addChildInstance(child);

            if(!child._unnamedEditableParent && child.inheritState) {
                child.state = this.state;
            }

            child.$on('ready', this.onChildReady);
            child.$on('destroyed', this.onChildDestroyed);
        },
        onChildReady(child) {
            this.childReady(child);
        },
        childReady(child) {
            if(child.baseName != undefined || this.handleUnnamedChild(child)) {
                let childData = (child.baseName in this.childrenData) ? this.childrenData[child.baseName] : undefined;

                this.initEditableChild(child, childData);
            }
        },
        onChildDestroyed(child) {
            this.childDestroyed(child);
        },
        childDestroyed(child) {
            this.removeChildInstance(child);
        },
        handleUnnamedChild(child) {
            if(child.childrenInstances) {
                child.allChildrenInstances.forEach(c => {
                    c._unnamedEditableParent = child;
                    this.childCreated(c);

                    if(c.isReady) {
                        this.childReady(c);
                    }
                });

                return false;
            }

            return true;
        },
        addChildInstance(child) {
            if(!(child.baseName in this.childrenInstances)) {
                Vue.set(this.childrenInstances, child.baseName, []);
            }

            child.index = this.allChildrenInstances.length;
            child.indexGrouped = this.childrenInstances[child.baseName].length;
            
            Vue.set(this.childrenInstances[child.baseName], child.indexGrouped, child);
        },
        removeChildInstance(child, removeInstanceAndData = false) {
            child.$off('datachange');
            
            if(removeInstanceAndData || !this.ignoreDestroyedChildren) {
                if(child.baseName in this.childrenInstances) {
                    // delete child instance
                    Vue.delete(this.childrenInstances[child.baseName], child.indexGrouped);

                    // re-index other instances
                    this.childrenInstances[child.baseName].forEach((c, i) => {
                        c.indexGrouped = i;
                    });
                    this.allChildrenInstances.forEach((c, i) => {
                        if(c.index > child.index) {
                            c.index--;
                        }
                    });
                }

                if((removeInstanceAndData || this.updateDataOnChildRemove) && child.baseName in this.childrenData) {
                    // delete child data
                    if(Array.isArray(this.childrenData[child.baseName])) {
                        const childDataIndex = this.getChildDataIndex(child);
                        Vue.delete(this.childrenData[child.baseName], childDataIndex);
                    } else {
                        Vue.delete(this.childrenData, child.baseName);
                    }
                }
            }
        },
        validate(showFails = false, onSuccess = null, onFailure = null) {
            let children = this.allChildrenInstances;
            let childrenValidationInfos = {};
            let result = true;
            const failedRules = {};
            const errors = {};

            children.forEach(child => {
                childrenValidationInfos[child.baseName] = {
                    validation: child.validation ? child.validation : (child.baseName in this.childrenValidations ? this.childrenValidations[child.baseName] : undefined),
                    namePath: child.namePath,
                };
            });

            children.forEach(child => {
                let validation = child.baseName in childrenValidationInfos ? childrenValidationInfos[child.baseName].validation : null;
                child.validationResult = null;

                if(validation && validation.length) {
                    const validationResult = validate(validation, child.formData, child.namePath, this.childrenData, childrenValidationInfos);
                    child.validationResult = validationResult === true ? true : false;

                    if(!child.validationResult) {
                        failedRules[child.baseName] = validationResult;

                        if(showFails) {
                            child.focused();

                            const childErrors = [];

                            for(const failedRule of validationResult) {
                                let errorMessage = null;

                                if(typeof this.childrenValidationMessages == 'function') {
                                    errorMessage = this.childrenValidationMessages(child.baseName, failedRule, child, childrenValidationInfos[child.baseName]);
                                } else {
                                    if(child.baseName in this.childrenValidationMessages) {
                                        if(typeof this.childrenValidationMessages[child.baseName] == 'string') {
                                            errorMessage = this.childrenValidationMessages[child.baseName];
                                        } else if(failedRule in this.childrenValidationMessages[child.baseName]) {
                                            errorMessage = this.childrenValidationMessages[child.baseName][failedRule];
                                        } else if('_common' in this.childrenValidationMessages[child.baseName]) {
                                            errorMessage = this.childrenValidationMessages[child.baseName]['_common'];
                                        }
                                    }
                                }

                                if(errorMessage === null) {
                                    if(typeof this.ruleValidationMessages == 'function') {
                                        errorMessage = this.ruleValidationMessages(failedRule, child.baseName, child, childrenValidationInfos[child.baseName]);
                                    } else {
                                        if(failedRule in this.ruleValidationMessages) {
                                            errorMessage = this.ruleValidationMessages[failedRule];
                                        } else if('_common' in this.ruleValidationMessages) {
                                            errorMessage = this.ruleValidationMessages['_common'];
                                        }
                                    }
                                }

                                if(errorMessage) {
                                    childErrors.push(errorMessage);
                                }
                            }

                            if(childErrors.length) {
                                errors[child.baseName] = childErrors;
                            }
                        }
                    }
                }

                if(child.hasEditableChildren) {
                    child.validationResult = (child.validationResult === null || child.validationResult) && child.validate(showFails);
                }
                
                if(child.validationResult !== null) {
                    result = result && child.validationResult;
                }
            });

            if(result && onSuccess) {
                onSuccess();
            } else if(!result) {
                if(onFailure) {
                    const newErrors = onFailure(failedRules, errors);
                    errors = newErrors !== undefined ? newErrors : errors;
                }

                if(showFails && errors && Object.values(errors).length) {
                    this.setErrors(errors);
                }
            }

            return result;
        },
        setErrors(errors) {
            if(errors === null) {
                this.alerts = [];
                this.allChildrenInstances.forEach(child => {
                    child.setErrors(null);
                });
            } else if(typeof errors == 'string') {
                this.alerts = [ { type: 'error', text: errors } ];
            } else {
                this.alerts = [];

                if(Array.isArray(errors)) {
                    errors.forEach(error => {
                        this.alerts.push(typeof error == 'string' ? { type: 'error', text: error } : error);
                    });
                } else {
                    if(Object.keys(errors).every(k => !isNaN(k))) {
                        errors = { undefined: errors };
                    }

                    Object.entries(errors).forEach(([ key, value ]) => {
                        if(key in this.childrenInstances) {
                            if(this.childrenAlwaysMultiple || this.childrenInstances[key].length > 1) {
                                for(let i in this.childrenInstances[key]) {
                                    if(i in value) {
                                        this.childrenInstances[key][i].setErrors(value[i]);
                                    }
                                }
                            } else {
                                this.childrenInstances[key][0].setErrors(value);
                            }
                        }
                    });
                }
            }
        },
        initEditableChild(child, childData = undefined) {
            if(this.childIsInGroup(child) && Array.isArray(childData) && child.idValue === undefined) {
                childData = childData.length >= (child.indexGrouped + 1) ? childData[child.indexGrouped] : undefined;
            }

            child.$on('datachange', ev => {
                this.childChanged(ev.source, ev.data);
            });
            child.$on('user-input', ev => {

                this.$emit('user-input', ev);
            });

            if(childData != undefined) {
                child.setData(childData);
            } else {
                this.updateDataFromChild(child, undefined, this.bubbleChanges);
            }
        },
        childChanged(child, newData) {
            if(!(child.baseName in this.childrenInstances)) {
                return false;
            }

            this.updateDataFromChild(child, newData, this.bubbleChanges);
        },
        getEditableChildren(name = null, asObject = false) {
            let children = this.findEditableChildren(
                this.$children, 
                child => child.$options && child.$options._componentTag.indexOf('editable') === 0 && child.name != undefined, 
                name
            );

            if(asObject) {
                let childrenObject = {};
                
                if(Array.isArray(children)) {
                    children.forEach(child => {
                        childrenObject[child.baseName] = child;
                    });
                } else {
                    childrenObject[children.name] = children;
                }

                return childrenObject;
            } else {
                return children;
            }
        },
        findEditableChildren(searchInChildren, isEditableChildCallback, filterName = null, getChildCallback = null) {
            let children = [];

            searchInChildren.forEach(child => {
                if(isEditableChildCallback(child)) {
                    if(getChildCallback) {
                        child = getChildCallback(child);
                    }

                    if(filterName && child.baseName === filterName) {
                        children = [ child ];
                        return false;
                    } else {
                        children.push(child);
                    }
                } else if(child.$children) {
                    children = children.concat(this.findEditableChildren(child.$children, isEditableChildCallback, filterName, getChildCallback));
                }
            });

            return filterName && children.length ? children[0] : children;
        },
        getChildDataIndex(child) {
            let index = child.indexGrouped;

            if(child.idValue !== undefined) {
                index = this.childrenData[child.baseName].findIndex(d => d && typeof d == 'object' && child.idKey in d && d[child.idKey] == child.idValue);
            }

            return index;
        },
        updateDataFromChild(child, newData = undefined, triggerDataChanged = true) {
            if(newData === undefined) {
                newData = child.getData();
            }

            if(this.childIsInGroup(child)) {
                if(!(child.baseName in this.childrenData)) {
                    Vue.set(this.childrenData, child.baseName, [ newData ]);
                } else {
                    const childDataIndex = this.getChildDataIndex(child);

                    this.childrenData[child.baseName][childDataIndex] = newData;
                }
            } else {
                Vue.set(this.childrenData, child.baseName, newData);
            }

            if(triggerDataChanged && this.ready && 'dataChanged' in this) {
                this.dataChanged();
            }
        },
        resortChildrenInstances() {
            const allChildrenSorted = this.allChildrenInstances;

            this.childrenInstances = {};

            for(const child of allChildrenSorted) {
                this.addChildInstance(child);
            }
        },
        /**
         * Update the childrenData object based on the current set children instances.
         */
        updateDataFromChildren() {
            this.childrenData = {};

            for(const child of this.allChildrenInstances) {
                this.updateDataFromChild(child, child.getData(), false);
            }

            if('dataChanged' in this) {
                this.dataChanged();
            }
        },
        removeEmptyChildren() {
            for(const child of this.allChildrenInstances) {
                if('removeEmptyChildren' in child) {
                    child.removeEmptyChildren();
                }

                const childData = child.getData();

                if(childData === null || childData === undefined || childData.toString().trim().length == 0) {
                    this.removeChildInstance(child, true);
                }
            }

            this.dataChanged();
        },
        childIsInGroup(child) {
            return this.childrenAlwaysMultiple || this.childrenInstances[child.baseName].length > 1 || child.idValue !== undefined;
        },
        set(name, data, triggerChange = false, byUserInput = false) {
            let targetChildren = (name in this.childrenInstances) ? this.childrenInstances[name] : [];
                    
            if((this.childrenAlwaysMultiple || targetChildren.length > 1) && Array.isArray(data)) {
                for(let i = 0; i < data.length; i++) {
                    this.setChildData(name, data, i);
                }
            } else {
                this.setChildData(name, data);
            }

            if(triggerChange) {
                this.dataChanged(byUserInput);
            }
        },
        setData(newData, merge = false, recursive = true) {
            let data = this._setData(newData);
            
            this.setChildrenData(data, merge, recursive);
        },
        setChildrenData(data, merge = false, recursive = true) {
            if(isPlainObject(data)) {
                this.childrenData = merge ? Object.assign(this.childrenData, data) : data;
            } else {
                this.childrenData[undefined] = merge && undefined in this.childrenData ? Object.assign(this.childrenData[undefined], data) : data;
            }
            
            if(recursive && data) {
                if(isPlainObject(data)) {
                    if(this.defaultChildValue !== undefined) {
                        Object.keys(this.childrenInstances).forEach(childrenName => {
                            if(!(childrenName in data)) {
                                let defaultChildrenData = this.defaultChildValue;

                                if(this.childrenAlwaysMultiple || this.childrenInstances[childrenName].length > 1) {
                                    defaultChildrenData = [];
                                    for(let i = 0; i < this.childrenInstances[childrenName].length; i++) {
                                        defaultChildrenData.push(this.defaultChildValue);
                                    }
                                }

                                data[childrenName] = defaultChildrenData;
                            }
                        });
                    }

                    Object.keys(data).forEach(name  => {
                        this.set(name, data[name]);
                    });
                } else {
                    this.set(undefined, data);
                }
            }
        },
        setChildData(name, data, index = null) {
            const targetChildren = (name in this.childrenInstances) ? this.childrenInstances[name] : null;
            const childIndex = index === null ? 0 : index;

            if(targetChildren && targetChildren.length > childIndex) {
                let targetChild = targetChildren[childIndex];
                
                if(index !== null && targetChild.idValue === undefined && Array.isArray(data) && data.length > index) {
                    data = data[index];
                }

                if(targetChild.isReady) {
                    targetChild.setData(data);
                }
            } else if('addChild' in this) {
                this.addChild(name, childIndex, data, false);
            }
        },
        prepareNewData(data) {
            return data;
        },
        getData(data = undefined) {
            if(data === undefined) {
                data = Object.keys(this.childrenData).length == 1 && (undefined in this.childrenData) ? this.childrenData[undefined] : this.childrenData;
            }
            
            return this._getData(data);
        },
        onChangedByUserInputUpdated(newValue) {
            this.bubbleUpChangedByUserInput(newValue);
            this.trickleDownChangedByUserInput(newValue);
        },
        trickleDownChangedByUserInput(newValue) {
            this.allChildrenInstances.forEach(child => child.changedByUserInput = newValue);
        }
    }
}

</script>